import { forwardRef } from 'react';
import { StyleSheet, type TextInput } from 'react-native';
import {
  MaskedTextInput,
  type MaskedTextInputProps,
} from 'react-native-mask-text';
import { theme, TYPE_CONFIG } from '@garnish/constants';

import { webOnlyStyles } from '../../utils';
import { VStack } from '../Stack';
import { TagAlert } from '../TagAlert';

// ─────────────────────────────────────────────────────────────────────────────

const MaskedTextFieldRaw = forwardRef<TextInput, Props>((props, ref) => {
  const { style, notice, noticePalette, ...restProps } = props;

  const inputStyles = [styles.input, webStyles, style];

  return (
    <VStack gap={theme.spacing['2']}>
      <MaskedTextInput ref={ref} style={inputStyles} {...restProps} />

      {notice ? <TagAlert palette={noticePalette}>{notice}</TagAlert> : null}
    </VStack>
  );
});

const MaskedTextFieldCurrency = forwardRef<TextInput, Props>((props, ref) => {
  return (
    <MaskedTextFieldRaw
      ref={ref}
      type="currency"
      options={CURRENCY_OPTIONS}
      keyboardType="number-pad"
      {...props}
    />
  );
});

// ─────────────────────────────────────────────────────────────────────────────

export const MaskedTextField = {
  Raw: MaskedTextFieldRaw,
  Currency: MaskedTextFieldCurrency,
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  input: {
    ...TYPE_CONFIG.BODY['16'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
    paddingVertical: theme.spacing['4'],
    paddingHorizontal: theme.spacing['3'],
    borderBottomWidth: 0,
  },
});

const webStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CURRENCY_OPTIONS: MaskedTextInputProps['options'] = {
  prefix: '$',
  decimalSeparator: '.',
  groupSeparator: ',',
  precision: 2,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = MaskedTextInputProps & {
  notice?: string;
  noticePalette?: React.ComponentProps<typeof TagAlert>['palette'];
};
